import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getCollection } from '../services/firestoreService';
import { useBranch } from '../context/BranchContext';

// ═══════════════════════════════════════════════════════════════════════════════
// REGLAS DE CONTEO (definidas con el usuario)
//
// 1. Cada ficha SOAP (clinicalRecords) es un caso. Si sus diagnosticos pertenecen
//    a Z grupos de enfermedad distintos, el caso aporta 1 al N de CADA uno de los
//    Z grupos.
// 2. Cada egreso hospitalario (egresosHospitalarios) es un caso y se cuenta UNA
//    sola vez, en el grupo del diagnostico de egreso (o de ingreso si no hay).
// 3. Cada protocolo quirurgico (surgeryProtocols) es un caso y se cuenta UNA sola
//    vez, en el grupo del diagnostico pre/post operatorio o, en su defecto, el
//    que corresponde al tipo de procedimiento.
// 4. Fallecimientos: campo 'fallecido' de egresosHospitalarios.
// 5. Complicaciones: solo hospitalizacion y cirugia, segun los campos marcados en
//    cada coleccion.
// 6. Los grupos de enfermedad son los valores de 'group' del catalogo 'diagnoses',
//    canonicalizados para fusionar variantes que nombran lo mismo.
// ═══════════════════════════════════════════════════════════════════════════════

const SIN_CLASIFICAR = 'Sin clasificar';

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// El catalogo 'diagnoses' tiene 28 valores de group, de los cuales 9 pares nombran
// el mismo grupo clinico con distinta redaccion. Se fusionan en 19 grupos canonicos.
const GROUP_CANON_PAIRS = [
  ['Cardiología y Respiratorio', 'Cardiología y Respiratorio'],
  ['Enfermedades Infecciosas', 'Enfermedades Infecciosas'],
  ['Enfermedades Infecciosas (Virales, Bacterianas y Sistémicas)', 'Enfermedades Infecciosas'],
  ['Neurología', 'Neurología'],
  ['Hematología e Inmunología', 'Hematología e Inmunología'],
  ['Dermatología', 'Dermatología'],
  ['Dermatología (Piel y Oídos)', 'Dermatología'],
  ['Nefrología y Urología', 'Nefrología y Urología'],
  ['Sistema Urinario y Renal', 'Nefrología y Urología'],
  ['Odontología', 'Odontología'],
  ['Odontología (Dientes y Boca)', 'Odontología'],
  ['Endocrinología', 'Endocrinología'],
  ['Gastroenterología y Digestivo', 'Gastroenterología y Digestivo'],
  ['Gastrointestinal', 'Gastroenterología y Digestivo'],
  ['Oftalmología', 'Oftalmología'],
  ['Oftalmología (Ojos)', 'Oftalmología'],
  ['Oncología', 'Oncología'],
  ['Trauma y Urgencias', 'Trauma y Urgencias'],
  ['Trauma, Urgencias y Cuidados Críticos', 'Trauma y Urgencias'],
  ['Ortopedia y Musculoesquelético', 'Ortopedia y Musculoesquelético'],
  ['Musculoesquelético (Trauma y Ortopedia)', 'Ortopedia y Musculoesquelético'],
  ['Reproducción y Ginecología', 'Reproducción y Ginecología'],
  ['Reproducción y Genitourinario', 'Reproducción y Ginecología'],
  ['Toxicología (Venenos e Intoxicaciones)', 'Toxicología'],
  ['Comportamiento (Etología Clínica)', 'Etología Clínica'],
  ['Nutrición y Metabólico', 'Nutrición y Metabólico'],
  ['Cirugía / Complicaciones postoperatorias', 'Cirugía y Complicaciones Postoperatorias'],
  ['Procedimientos Generales / Medicina Preventiva', 'Procedimientos Generales / Medicina Preventiva'],
];

const GROUP_CANON = Object.fromEntries(
  GROUP_CANON_PAIRS.map(([raw, canon]) => [normalize(raw), canon])
);

// Lista de grupos canonicos en orden de despliegue (alfabetico, 'Sin clasificar' al final)
const CANON_GROUPS = Array.from(new Set(GROUP_CANON_PAIRS.map(([, c]) => c)))
  .sort((a, b) => a.localeCompare(b, 'es'));

const canonicalGroup = (raw) => {
  if (!raw) return null;
  const n = normalize(raw);
  if (GROUP_CANON[n]) return GROUP_CANON[n];
  // Un grupo nuevo en el catalogo se muestra tal cual en vez de perderse
  return String(raw).trim() || null;
};

// ── Clasificacion de procedimientos quirurgicos ───────────────────────────────
// Se usa solo cuando el protocolo no trae un diagnostico resoluble. Los prefijos
// son los del catalogo de procedimientos (ej. "ORT-005 - Reduccion abierta...").
const PROC_PREFIX_GROUP = {
  OVH: 'Reproducción y Ginecología',
  ORQ: 'Reproducción y Ginecología',
  CES: 'Reproducción y Ginecología',
  VAG: 'Reproducción y Ginecología',
  MAM: 'Reproducción y Ginecología',
  ORT: 'Ortopedia y Musculoesquelético',
  MAS: 'Oncología',
  BAZ: 'Oncología',
  OFT: 'Oftalmología',
  URE: 'Nefrología y Urología',
  CIS: 'Nefrología y Urología',
  PRO: 'Nefrología y Urología',
  REN: 'Nefrología y Urología',
  GAS: 'Gastroenterología y Digestivo',
  INT: 'Gastroenterología y Digestivo',
  HEP: 'Gastroenterología y Digestivo',
  REC: 'Gastroenterología y Digestivo',
  ESO: 'Gastroenterología y Digestivo',
  LAP: 'Gastroenterología y Digestivo',
  HER: 'Gastroenterología y Digestivo',
  NEU: 'Neurología',
  CAB: 'Cardiología y Respiratorio',
  TOR: 'Cardiología y Respiratorio',
  DEN: 'Odontología',
  PIN: 'Dermatología',
  PIE: 'Dermatología',
  'CIR-COMP': 'Cirugía y Complicaciones Postoperatorias',
};

// Procedimientos escritos en texto libre, sin codigo (ej. "Ovariohisterectomia
// (Esterilizacion Hembra)"). Se evaluan en orden; la primera coincidencia gana.
const PROC_KEYWORD_GROUP = [
  [['ovariohisterectom', 'ovh', 'esterilizacion', 'orquiectom', 'castracion', 'cesarea', 'piometra', 'criptorquid', 'vaginoplast', 'episiotom'], 'Reproducción y Ginecología'],
  [['periodontal', 'dental', 'odontolog', 'extraccion dentaria', 'tartrectom'], 'Odontología'],
  [['masa tumoral', 'tumor', 'neoplas', 'mastectom', 'biopsia', 'nodulo', 'lipoma'], 'Oncología'],
  [['osteosintesis', 'amputacion', 'fijador', 'artrodesis', 'artrotom', 'femoral', 'tibial', 'pelvic', 'fractura', 'luxacion', 'ilio femoral', 'illio femoral', 'rotula', 'patela'], 'Ortopedia y Musculoesquelético'],
  [['enucleacion', 'tarsorrafia', 'ocular', 'cornea', 'parpado', 'conjuntiv', 'oftalm'], 'Oftalmología'],
  [['gastrotom', 'enterotom', 'laparotom', 'cuerpo extra', 'esplenectom', 'colecistect', 'hernior', 'hernia', 'intestin', 'gastr', 'recto', 'anal'], 'Gastroenterología y Digestivo'],
  [['uretrostom', 'cistotom', 'urolito', 'vejiga', 'renal', 'nefrect', 'prostat'], 'Nefrología y Urología'],
  [['hemilaminect', 'laminect', 'columna', 'medular', 'neurolog'], 'Neurología'],
  [['braquicefal', 'traqueo', 'paladar', 'narina', 'toracotom', 'diafragm'], 'Cardiología y Respiratorio'],
  [['sutura', 'herida', 'limpieza quirurgica', 'desbrida', 'evisceracion', 'dehiscencia', 'mordedura'], 'Trauma y Urgencias'],
  [['otohematoma', 'pinectom', 'conducto auditivo', 'dermatolog', 'piel'], 'Dermatología'],
];

// Cuando un registro no resuelve contra el catalogo de diagnosticos, se
// intenta clasificar por el texto libre del motivo, diagnostico o visita.
const FALLBACK_RULES = [
  {
    group: 'Control Tratamiento/Postquirúrgico',
    terms: [
      'control posquirurgico',
      'control post qx',
      'control post-cirugia',
      'seguimiento post tratamiento',
      'seguimiento postratamiento',
      'seguimiento post-tratamiento',
      'revision post-tratamiento',
      'revision postratamiento',
      'post-tratamiento',
      'postratamiento',
    ],
  },
  {
    group: 'Procedimientos Generales / Medicina Preventiva',
    terms: [
      'vacuna',
      'vacunacion',
      'vacunar',
      'esquema de vacunacion',
      'clinicamente sano',
      'clinicamente normal',
      'apto para cirugia',
      'apto quirurgico',
      'sano',
      'control sano',
      'chequeo preventivo',
      'medicina preventiva',
    ],
  },
  {
    group: 'Sin diagnóstico ingresado',
    terms: [
      'sin diagnostico',
      'ficha sin diagnosticos',
      'sin diagnosticos',
      'diagnostico no especificado',
      'no especificado',
    ],
  },
];

const matchFallback = (text) => {
  const n = normalize(text);
  if (!n) return null;
  for (const rule of FALLBACK_RULES) {
    if (rule.terms.some((t) => n.includes(normalize(t)))) return rule.group;
  }
  return null;
};

// ── Utilidades de parseo ──────────────────────────────────────────────────────
// Los diagnosticos de surgeryProtocols vienen como "25562003 - Fractura ósea" y
// pueden traer varios separados por " | ".
const splitMulti = (value) =>
  String(value || '')
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean);

const parseCodedLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { code: '', name: '' };
  const m = raw.match(/^([A-Za-z0-9][A-Za-z0-9-]*)\s*[-–—]\s*(.+)$/s);
  if (m) return { code: m[1].trim(), name: m[2].trim() };
  return { code: '', name: raw };
};

const getDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  const ms = Date.parse(String(value));
  return Number.isNaN(ms) ? null : new Date(ms);
};

const daysBetween = (a, b) => {
  const d1 = getDate(a);
  const d2 = getDate(b);
  if (!d1 || !d2) return 0;
  return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
};

const formatDate = (value) => {
  const d = getDate(value);
  return d ? d.toLocaleDateString('es-CL') : '';
};

const getYearMonth = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return { year: Number(value.slice(0, 4)), month: Number(value.slice(5, 7)) };
  }
  const d = getDate(value);
  if (!d) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

// ── Exportacion CSV ───────────────────────────────────────────────────────────
const escapeCsv = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCsv = (rows) => rows.map((r) => r.map(escapeCsv).join(',')).join('\n');
const downloadFile = (filename, content) => {
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getYearOptions = () => {
  const current = new Date().getFullYear();
  const list = [];
  for (let y = current; y >= 2020; y -= 1) list.push(y);
  return list;
};

const MONTH_OPTIONS = [
  { value: 'all', label: 'Todo el año' },
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const MESES_LABEL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const emptyMetrics = (grupo) => ({
  grupo,
  n: 0,
  nConsulta: 0,
  nHospitalizacion: 0,
  nCirugia: 0,
  farmacologico: 0,
  diasHospitalizacion: 0,
  complicaciones: 0,
  fallecimiento: 0,
  altaMedica: 0,
  derivado: 0,
  noRegresa: 0,
});

const METRIC_KEYS = [
  'n', 'nConsulta', 'nHospitalizacion', 'nCirugia', 'farmacologico',
  'diasHospitalizacion', 'complicaciones', 'fallecimiento', 'altaMedica',
  'derivado', 'noRegresa',
];

const HospitalCasuistryTab = () => {
  const { activeBranchId, accessibleBranches, filterByBranch } = useBranch();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState('all');
  const [catalog, setCatalog] = useState([]);
  const [clinicalRecords, setClinicalRecords] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [surgeryProtocols, setSurgeryProtocols] = useState([]);
  const [onlineBookings, setOnlineBookings] = useState([]);
  const [patientsMap, setPatientsMap] = useState(new Map());
  const [surgeriesMap, setSurgeriesMap] = useState(new Map());
  const [hospitalizationsMap, setHospitalizationsMap] = useState(new Map());
  const [capacitiesMap, setCapacitiesMap] = useState(new Map());
  const [clinicsMap, setClinicsMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Catalogo maestro de diagnosticos (fuente de verdad de los grupos)
  useEffect(() => {
    getCollection('diagnoses')
      .then((data) => setCatalog(data || []))
      .catch((err) => {
        console.error(err);
        setError('Error cargando el catálogo de diagnósticos.');
      });
  }, []);

  // Registros del periodo. Cada coleccion tiene su propio campo de fecha:
  //   clinicalRecords -> createdAt (Timestamp)
  //   egresosHospitalarios -> fechaIngreso (string YYYY-MM-DD)
  //   surgeryProtocols -> date (string YYYY-MM-DD)
  useEffect(() => {
    const load = async () => {
      if (!year) return;
      setLoading(true);
      setError('');
      try {
        const startDate = new Date(year, 0, 1, 0, 0, 0, 0);
        const endDate = new Date(year, 11, 31, 23, 59, 59, 999);
        const startStr = `${year}-01-01`;
        const endStr = `${year}-12-31`;

        const prevYearStart = new Date(year - 1, 0, 1, 0, 0, 0, 0);
        const [clinicalSnap, egresosSnap, protocolsSnap, bookingsSnap, patientsData, surgeriesData, hospitalizationsData, capacitiesData, clinicsData] = await Promise.all([
          getDocs(query(
            collection(db, 'clinicalRecords'),
            where('createdAt', '>=', Timestamp.fromDate(startDate)),
            where('createdAt', '<=', Timestamp.fromDate(endDate))
          )),
          getDocs(query(
            collection(db, 'egresosHospitalarios'),
            where('fechaIngreso', '>=', startStr),
            where('fechaIngreso', '<=', endStr)
          )),
          getDocs(query(
            collection(db, 'surgeryProtocols'),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
          )),
          getDocs(query(
            collection(db, 'onlineBookings'),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
          )),
          getCollection('patients'),
          getDocs(query(
            collection(db, 'surgeries'),
            where('date', '>=', startStr),
            where('date', '<=', endStr)
          )),
          getDocs(query(
            collection(db, 'hospitalizations'),
            where('admissionAt', '>=', Timestamp.fromDate(prevYearStart)),
            where('admissionAt', '<=', Timestamp.fromDate(endDate))
          )),
          getCollection('capacities'),
          getCollection('clinics'),
        ]);

        const mapDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClinicalRecords(mapDocs(clinicalSnap));
        setEgresos(mapDocs(egresosSnap));
        setSurgeryProtocols(mapDocs(protocolsSnap));
        setOnlineBookings(mapDocs(bookingsSnap));
        const pMap = new Map();
        (patientsData || []).forEach((p) => { if (p.id) pMap.set(p.id, p); });
        setPatientsMap(pMap);
        const sMap = new Map();
        mapDocs(surgeriesData).forEach((s) => { if (s.id) sMap.set(s.id, s); });
        setSurgeriesMap(sMap);
        const hMap = new Map();
        mapDocs(hospitalizationsData).forEach((h) => { if (h.id) hMap.set(h.id, h); });
        setHospitalizationsMap(hMap);
        const cMap = new Map();
        (capacitiesData || []).forEach((c) => { if (c.id) cMap.set(c.id, c); });
        setCapacitiesMap(cMap);
        const clMap = new Map();
        (clinicsData || []).forEach((c) => { if (c.id) clMap.set(c.id, c); });
        setClinicsMap(clMap);
      } catch (err) {
        console.error(err);
        setError('Error cargando registros del periodo.');
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // ── Indices del catalogo: codigo/id/nombre -> grupo canonico ────────────────
  const { groupByCode, groupByName } = useMemo(() => {
    const byCode = new Map();
    const byName = new Map();
    for (const d of catalog) {
      const g = canonicalGroup(d.group);
      if (!g) continue;
      if (d.code) byCode.set(String(d.code).trim(), g);
      if (d.id) byCode.set(String(d.id), g);
      const n = normalize(d.diagnosis);
      if (n && !byName.has(n)) byName.set(n, g);
    }
    return { groupByCode: byCode, groupByName: byName };
  }, [catalog]);

  // Resuelve el grupo de un diagnostico, sea objeto {codigo,descripcion,grupo} o
  // {code,name}, o una etiqueta "codigo - nombre".
  const resolvers = useMemo(() => {
    const fromParts = (code, name, ownGroup) => {
      // 1. Grupo explicito del propio registro (casi siempre vacio en SOAP)
      if (ownGroup) {
        const g = canonicalGroup(ownGroup);
        if (g) return { grupo: g, via: 'grupo del registro' };
      }
      // 2. Codigo contra el catalogo (via principal: 83% de los SOAP)
      const c = String(code || '').trim();
      if (c && groupByCode.has(c)) return { grupo: groupByCode.get(c), via: `código ${c}` };
      // 3. Nombre del diagnostico contra el catalogo
      const n = normalize(name);
      if (n && groupByName.has(n)) return { grupo: groupByName.get(n), via: 'nombre del diagnóstico' };
      return null;
    };

    // Diagnostico de clinicalRecords: {codigo, descripcion, grupo, tipo}
    const fromSoapDx = (dx) => {
      if (!dx) return null;
      return fromParts(dx.codigo || dx.code, dx.descripcion || dx.name, dx.grupo || dx.group);
    };

    // Diagnostico de egresos: {code, name} o {id, code, name}
    const fromEgresoDx = (dx) => {
      if (!dx) return null;
      const byId = dx.id && groupByCode.has(String(dx.id))
        ? { grupo: groupByCode.get(String(dx.id)), via: 'id de catálogo' }
        : null;
      return fromParts(dx.code, dx.name || dx.diagnosis, dx.group) || byId;
    };

    // Diagnostico de surgeryProtocols: "25562003 - Fractura ósea | 302837000 - Osteosarcoma"
    const fromLabel = (value) => {
      for (const part of splitMulti(value)) {
        const { code, name } = parseCodedLabel(part);
        const hit = fromParts(code, name, null);
        if (hit) return hit;
      }
      return null;
    };

    // Tipo de procedimiento: por prefijo de codigo y, si no, por palabras clave
    const fromProcedure = (procedureType) => {
      for (const part of splitMulti(procedureType)) {
        const { code } = parseCodedLabel(part);
        if (code) {
          const upper = code.toUpperCase();
          if (PROC_PREFIX_GROUP[upper]) {
            return { grupo: PROC_PREFIX_GROUP[upper], via: `procedimiento ${upper}` };
          }
          const pref = (upper.match(/^([A-Z]+)/) || [])[1];
          if (pref && PROC_PREFIX_GROUP[pref]) {
            return { grupo: PROC_PREFIX_GROUP[pref], via: `prefijo de procedimiento ${pref}` };
          }
        }
      }
      const n = normalize(procedureType);
      if (n) {
        for (const [keys, grupo] of PROC_KEYWORD_GROUP) {
          if (keys.some((k) => n.includes(normalize(k)))) {
            return { grupo, via: 'nombre del procedimiento' };
          }
        }
      }
      return null;
    };

    return { fromSoapDx, fromEgresoDx, fromLabel, fromProcedure };
  }, [groupByCode, groupByName]);

  // ── Agregacion ─────────────────────────────────────────────────────────────
  const { rows, total, details, unresolved } = useMemo(() => {
    const selectedMonth = month === 'all' ? null : Number(month);
    const inSelectedMonth = (m) => selectedMonth === null || m === selectedMonth;

    const fClinical = filterByBranch(clinicalRecords, { includeUnassigned: true }).filter((rec) => {
      const d = getDate(rec.createdAt);
      return d && d.getFullYear() === year && inSelectedMonth(d.getMonth() + 1);
    });
    const fEgresos = filterByBranch(egresos, { includeUnassigned: true }).filter((eg) => {
      const ymd = getYearMonth(eg.fechaIngreso);
      return ymd && ymd.year === year && inSelectedMonth(ymd.month);
    });
    const fProtocols = filterByBranch(surgeryProtocols, { includeUnassigned: true }).filter((p) => {
      const ymd = getYearMonth(p.date);
      return ymd && ymd.year === year && inSelectedMonth(ymd.month);
    });

    const agg = {};
    const ensure = (grupo) => {
      if (!agg[grupo]) agg[grupo] = emptyMetrics(grupo);
      return agg[grupo];
    };
    for (const g of CANON_GROUPS) ensure(g);

    const detail = [];
    const unres = {};
    const addUnresolved = (fuente, valor) => {
      const key = `${fuente} :: ${String(valor || '(vacío)').trim() || '(vacío)'}`;
      unres[key] = (unres[key] || 0) + 1;
    };

    // ── 1. Fichas SOAP: 1 caso, pero N se suma en cada grupo distinto ────────
    for (const rec of fClinical) {
      const dxList = Array.isArray(rec.diagnosticos) ? rec.diagnosticos : [];
      const grupos = new Map(); // grupo -> via (primer diagnostico que lo aporto)
      const sinResolver = [];

      for (const dx of dxList) {
        const hit = resolvers.fromSoapDx(dx);
        if (hit) {
          if (!grupos.has(hit.grupo)) grupos.set(hit.grupo, hit.via);
        } else {
          sinResolver.push(dx?.descripcion || dx?.codigo || '');
        }
      }

      // Si ningun diagnostico resolvio, se prueba el texto libre antes de "Sin clasificar"
      if (grupos.size === 0) {
        const allText = [
          ...dxList.map((d) => `${d?.codigo || ''} ${d?.descripcion || ''} ${d?.tipo || ''}`),
          rec.tipoVisita || '',
          rec.motivoConsulta || '',
        ].join(' ');
        const fallback = matchFallback(allText);
        if (fallback) {
          grupos.set(fallback, 'clasificación por texto libre');
        } else {
          grupos.set(SIN_CLASIFICAR, dxList.length ? 'diagnóstico no clasificable' : 'sin diagnóstico');
          sinResolver.forEach((v) => addUnresolved('SOAP', v));
          if (!dxList.length) addUnresolved('SOAP', '(ficha sin diagnósticos)');
        }
      }

      const tratamientos = Array.isArray(rec.tratamientos) ? rec.tratamientos : [];
      const conFarmaco = tratamientos.some((t) =>
        ['medicamento', 'farmacologico'].includes(normalize(t?.tipo))
      );

      for (const [grupo, via] of grupos) {
        const row = ensure(grupo);
        row.n += 1;
        row.nConsulta += 1;
        if (conFarmaco) row.farmacologico += 1;

        detail.push({
          fuente: 'clinicalRecords',
          id: rec.id,
          fecha: formatDate(rec.createdAt),
          grupo,
          via,
          gruposDelCaso: grupos.size,
          paciente: rec.patientName || '',
          pacienteId: rec.patientId || '',
          profesional: rec.vetNombre || '',
          diagnosticos: dxList.map((d) => `${d?.codigo || ''} ${d?.descripcion || ''}`.trim()).join(' | '),
          tipoVisita: rec.tipoVisita || '',
          farmacologico: conFarmaco ? 1 : 0,
          diasHospitalizacion: 0,
          complicaciones: 0,
          fallecimiento: 0,
          resultado: '',
        });
      }
    }

    // ── 2. Egresos hospitalarios: 1 caso en 1 solo grupo ────────────────────
    for (const eg of fEgresos) {
      const dxEgreso = eg.diagnosticoEgreso;
      const dxIngreso = eg.diagnosticoIngreso;
      const dxPrincipal = eg.diagnosticoPrincipal;

      let hit = resolvers.fromEgresoDx(dxEgreso);
      let origen = 'diagnóstico de egreso';
      if (!hit) { hit = resolvers.fromEgresoDx(dxIngreso); origen = 'diagnóstico de ingreso'; }
      if (!hit) { hit = resolvers.fromEgresoDx(dxPrincipal); origen = 'diagnóstico principal'; }
      // Ultimo recurso: el procedimiento principal indica el area
      if (!hit && eg.procedimientoPrincipal) {
        const p = eg.procedimientoPrincipal;
        hit = resolvers.fromProcedure(`${p.code || ''} - ${p.name || ''}`);
        origen = 'procedimiento principal';
      }

      if (!hit) {
        const allText = [
          `${dxEgreso?.code || ''} ${dxEgreso?.name || dxEgreso?.diagnosis || ''}`,
          `${dxIngreso?.code || ''} ${dxIngreso?.name || dxIngreso?.diagnosis || ''}`,
          `${dxPrincipal?.code || ''} ${dxPrincipal?.name || dxPrincipal?.diagnosis || ''}`,
          eg.tipoAltaNormalizado || '',
          eg.condicionAlta || '',
        ].join(' ');
        const fallback = matchFallback(allText);
        if (fallback) {
          hit = { grupo: fallback, via: 'por texto libre' };
          origen = 'caso sin diagnóstico en catálogo';
        } else {
          const dx = dxEgreso || dxIngreso || dxPrincipal || {};
          addUnresolved('Egreso', `${dx.code || ''} ${dx.name || dx.diagnosis || ''}`.trim());
          origen = 'no clasificable';
        }
      }

      const grupo = hit ? hit.grupo : SIN_CLASIFICAR;

      // Dias: se prefiere el campo ya calculado del import
      const dias = typeof eg.diasHospitalizacion === 'number'
        ? eg.diasHospitalizacion
        : daysBetween(eg.fechaIngreso, eg.fechaAlta);

      const complico = eg.complicaciones === true
        || eg.complicacionQx === true
        || eg.complicacionPostQx === true;

      const fallecio = eg.fallecido === true || eg.FALLECIDO === true;

      // 'tipoAltaNormalizado' es el campo ya normalizado por el import; el texto
      // libre de 'condicionAlta' solo se usa si el primero no esta.
      const tipoAlta = normalize(eg.tipoAltaNormalizado || eg.condicionAlta);
      let altaMedica = 0, derivado = 0, noRegresa = 0;
      if (!fallecio) {
        if (tipoAlta.includes('alta medica') || tipoAlta.includes('alta a domicilio') || tipoAlta.includes('alta condicional') || tipoAlta.includes('cuidados paliativos')) altaMedica = 1;
        else if (tipoAlta.includes('derivacion') || tipoAlta.includes('otro centro') || tipoAlta.includes('derivado')) derivado = 1;
        else if (tipoAlta.includes('peticion del tutor') || tipoAlta.includes('solicitada por') || tipoAlta.includes('voluntaria') || tipoAlta.includes('no regresa') || tipoAlta.includes('abandono')) noRegresa = 1;
      }

      const row = ensure(grupo);
      row.n += 1;
      row.nHospitalizacion += 1;
      row.diasHospitalizacion += dias;
      row.complicaciones += complico ? 1 : 0;
      row.fallecimiento += fallecio ? 1 : 0;
      row.altaMedica += altaMedica;
      row.derivado += derivado;
      row.noRegresa += noRegresa;

      const dxUsado = dxEgreso || dxIngreso || dxPrincipal || {};
      detail.push({
        fuente: 'egresosHospitalarios',
        id: eg.id,
        fecha: eg.fechaIngreso || '',
        grupo,
        via: hit ? `${origen} (${hit.via})` : origen,
        gruposDelCaso: 1,
        paciente: eg.patientName || `ficha ${eg.ficha || ''}`.trim(),
        pacienteId: eg.patientId || '',
        profesional: '',
        diagnosticos: `${dxUsado.code || ''} ${dxUsado.name || dxUsado.diagnosis || ''}`.trim(),
        tipoVisita: '',
        farmacologico: 0,
        diasHospitalizacion: dias,
        complicaciones: complico ? 1 : 0,
        fallecimiento: fallecio ? 1 : 0,
        resultado: eg.tipoAltaNormalizado || eg.condicionAlta || '',
      });
    }

    // ── 3. Protocolos quirurgicos: 1 caso en 1 solo grupo ───────────────────
    for (const p of fProtocols) {
      const sp = p.surgeonProtocol || {};

      let hit = resolvers.fromLabel(sp.preOpDiagnosis);
      let origen = 'diagnóstico preoperatorio';
      if (!hit) { hit = resolvers.fromLabel(sp.postOpDiagnosis); origen = 'diagnóstico postoperatorio'; }
      if (!hit) { hit = resolvers.fromProcedure(p.procedureType); origen = 'tipo de procedimiento'; }

      if (!hit) {
        const allText = [sp.preOpDiagnosis, sp.postOpDiagnosis, p.procedureType].join(' ');
        const fallback = matchFallback(allText);
        if (fallback) {
          hit = { grupo: fallback, via: 'por texto libre' };
          origen = 'caso sin diagnóstico en catálogo';
        } else {
          addUnresolved('Cirugía', `preOp="${sp.preOpDiagnosis || ''}" proc="${p.procedureType || ''}"`);
          origen = 'no clasificable';
        }
      }

      const grupo = hit ? hit.grupo : SIN_CLASIFICAR;

      // El texto de complicaciones suele traer "no", "ninguna" o vacio cuando no hubo
      const complText = String(sp.complications || '').trim();
      const complico = sp.hasComplications === true
        || (!!complText && !/^(no|ninguna?|sin complicaciones?|s\/c|n\/a|-|0)$/i.test(complText));

      const row = ensure(grupo);
      row.n += 1;
      row.nCirugia += 1;
      row.complicaciones += complico ? 1 : 0;

      detail.push({
        fuente: 'surgeryProtocols',
        id: p.id,
        fecha: p.date || '',
        grupo,
        via: hit ? `${origen} (${hit.via})` : origen,
        gruposDelCaso: 1,
        paciente: p.patientName || '',
        pacienteId: p.patientId || '',
        profesional: p.mainSurgeon || '',
        diagnosticos: sp.preOpDiagnosis || sp.postOpDiagnosis || '',
        tipoVisita: p.procedureType || '',
        farmacologico: 0,
        diasHospitalizacion: 0,
        complicaciones: complico ? 1 : 0,
        fallecimiento: 0,
        resultado: complico ? complText : '',
      });
    }

    // Filas: grupos canonicos con datos + cualquier grupo nuevo + Sin clasificar
    const extras = Object.keys(agg)
      .filter((g) => !CANON_GROUPS.includes(g) && g !== SIN_CLASIFICAR)
      .sort((a, b) => a.localeCompare(b, 'es'));
    const orderedNames = [...CANON_GROUPS, ...extras, SIN_CLASIFICAR];
    const ordered = orderedNames.map((g) => ensure(g));

    const tot = emptyMetrics('Total');
    for (const r of ordered) {
      for (const k of METRIC_KEYS) tot[k] += r[k];
    }

    const unresList = Object.entries(unres)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return { rows: ordered, total: tot, details: detail, unresolved: unresList };
  }, [clinicalRecords, egresos, surgeryProtocols, resolvers, filterByBranch, month, year]);

  const periodLabel = `${year}${month === 'all' ? '' : `-${String(month).padStart(2, '0')}`}`;

  const handleDownloadSummary = () => {
    const headers = ['grupo_enfermedad', 'N', 'N_consulta', 'N_hospitalizacion', 'N_cirugia', 'farmacologico', 'dias_hospitalizacion', 'complicaciones', 'fallecimiento', 'alta_medica', 'derivado', 'no_regresa'];
    const body = [...rows, total].map((r) => [r.grupo, ...METRIC_KEYS.map((k) => r[k])]);
    downloadFile(`casuistica_resumen_${periodLabel}.csv`, toCsv([headers, ...body]));
  };

  const handleDownloadDetail = () => {
    const headers = ['fuente', 'documento_id', 'fecha', 'grupo_enfermedad', 'criterio_clasificacion', 'grupos_del_caso', 'paciente', 'paciente_id', 'profesional', 'diagnosticos', 'tipo_visita_o_procedimiento', 'farmacologico', 'dias_hospitalizacion', 'complicaciones', 'fallecimiento', 'resultado'];
    const body = details.map((d) => [
      d.fuente, d.id, d.fecha, d.grupo, d.via, d.gruposDelCaso, d.paciente, d.pacienteId,
      d.profesional, d.diagnosticos, d.tipoVisita, d.farmacologico, d.diasHospitalizacion,
      d.complicaciones, d.fallecimiento, d.resultado,
    ]);
    downloadFile(`casuistica_detalle_${periodLabel}.csv`, toCsv([headers, ...body]));
  };

  const handleDownloadUnresolved = () => {
    const headers = ['fuente_y_valor', 'cantidad'];
    downloadFile(
      `casuistica_sin_clasificar_${periodLabel}.csv`,
      toCsv([headers, ...unresolved.map((u) => [u.name, u.count])])
    );
  };

  const unresolvedTotal = unresolved.reduce((acc, u) => acc + u.count, 0);

  // ── Atenciones y beneficiarios por sucursal ─────────────────────────────────
  const attentionBeneficiary = useMemo(() => {
    const displayBranches = accessibleBranches;
    const branchIds = displayBranches.map((b) => b.id);

    const resolveBranchIds = (it, source) => {
      if (it?.branchId) return [it.branchId];
      if (Array.isArray(it?.branchIds) && it.branchIds.length) return it.branchIds;

      if (source === 'surgery') {
        const s = it?.surgeryId && surgeriesMap.get(it.surgeryId);
        if (s?.branchId) return [s.branchId];
      }

      if (source === 'egreso') {
        const h = it?.hospitalizationId && hospitalizationsMap.get(it.hospitalizationId);
        if (h?.hospitalCapacityId) {
          const cap = capacitiesMap.get(h.hospitalCapacityId);
          if (Array.isArray(cap?.branchIds) && cap.branchIds.length) return cap.branchIds;
          if (cap?.branchId) return [cap.branchId];
        }
      }

      if (source === 'booking') {
        const c = it?.clinicId && clinicsMap.get(it.clinicId);
        if (Array.isArray(c?.branchIds) && c.branchIds.length) return c.branchIds;
        if (c?.branchId) return [c.branchId];
      }

      return [];
    };

    const belongs = (it, branchId, source) => {
      const ids = resolveBranchIds(it, source);
      if (ids.length === 0) return branchId === activeBranchId;
      return ids.includes(branchId);
    };

    const ownerRutOf = (it) => {
      if (it?.ownerRut) return String(it.ownerRut).trim().toLowerCase();
      const pid = it?.patientId || it?.petId;
      if (pid && patientsMap.has(pid)) {
        const p = patientsMap.get(pid);
        return p?.ownerRut ? String(p.ownerRut).trim().toLowerCase() : null;
      }
      return null;
    };

    const isAttended = (status) => ['completed', 'en_sala'].includes(status);

    const data = {};
    const monthSets = {};
    for (const bid of branchIds) {
      data[bid] = {};
      for (const m of MONTHS) {
        data[bid][m] = { consultas: 0, cirugias: 0, egresos: 0, total: 0, beneficiarios: new Set() };
      }
    }
    for (const m of MONTHS) {
      monthSets[m] = new Set();
    }

    for (const b of onlineBookings) {
      const ymd = getYearMonth(b.date);
      if (!ymd || ymd.year !== year || !isAttended(b.status)) continue;
      const m = ymd.month;
      const rut = ownerRutOf(b);
      for (const bid of branchIds) {
        if (belongs(b, bid, 'booking')) {
          data[bid][m].consultas += 1;
          data[bid][m].total += 1;
          if (rut) {
            data[bid][m].beneficiarios.add(rut);
            monthSets[m].add(rut);
          }
        }
      }
    }

    for (const p of surgeryProtocols) {
      const ymd = getYearMonth(p.date);
      if (!ymd || ymd.year !== year) continue;
      const m = ymd.month;
      const rut = ownerRutOf(p);
      for (const bid of branchIds) {
        if (belongs(p, bid, 'surgery')) {
          data[bid][m].cirugias += 1;
          data[bid][m].total += 1;
          if (rut) {
            data[bid][m].beneficiarios.add(rut);
            monthSets[m].add(rut);
          }
        }
      }
    }

    for (const eg of egresos) {
      const ymd = getYearMonth(eg.fechaIngreso);
      if (!ymd || ymd.year !== year) continue;
      const m = ymd.month;
      const rut = ownerRutOf(eg);
      for (const bid of branchIds) {
        if (belongs(eg, bid, 'egreso')) {
          data[bid][m].egresos += 1;
          data[bid][m].total += 1;
          if (rut) {
            data[bid][m].beneficiarios.add(rut);
            monthSets[m].add(rut);
          }
        }
      }
    }

    const totals = {};
    for (const m of MONTHS) {
      totals[m] = { consultas: 0, cirugias: 0, egresos: 0, total: 0, beneficiarios: monthSets[m].size };
      for (const bid of branchIds) {
        const d = data[bid][m];
        d.beneficiarios = d.beneficiarios.size;
        totals[m].consultas += d.consultas;
        totals[m].cirugias += d.cirugias;
        totals[m].egresos += d.egresos;
        totals[m].total += d.total;
      }
    }

    return { branches: displayBranches, data, totals };
  }, [onlineBookings, surgeryProtocols, egresos, patientsMap, year, activeBranchId, accessibleBranches, surgeriesMap, hospitalizationsMap, capacitiesMap, clinicsMap]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="casuistry-year-label">Año</InputLabel>
              <Select
                labelId="casuistry-year-label"
                value={year}
                label="Año"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {getYearOptions().map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="casuistry-month-label">Mes</InputLabel>
              <Select
                labelId="casuistry-month-label"
                value={month}
                label="Mes"
                onChange={(e) => setMonth(e.target.value)}
              >
                {MONTH_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 'grow' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadSummary} disabled={loading}>
                Resumen
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadDetail} disabled={loading || !details.length}>
                Detalle ({details.length})
              </Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<DownloadIcon />} onClick={handleDownloadUnresolved} disabled={loading || !unresolved.length}>
                Sin clasificar ({unresolvedTotal})
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Cómo se cuenta el N</AlertTitle>
            <Typography variant="body2" component="div">
              Cada <strong>ficha SOAP</strong> es un caso y aporta 1 al N de cada grupo de
              enfermedad distinto presente en sus diagnósticos. Cada{' '}
              <strong>egreso hospitalario</strong> y cada <strong>protocolo quirúrgico</strong>{' '}
              es un caso que se cuenta una sola vez, en el grupo de su diagnóstico
              (de egreso o de ingreso) o, si no hay, del procedimiento realizado.
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={`${clinicalRecords.length} fichas SOAP`} />
              <Chip size="small" label={`${egresos.length} egresos`} />
              <Chip size="small" label={`${surgeryProtocols.length} protocolos quirúrgicos`} />
              <Chip size="small" color="primary" label={`N total: ${total.n}`} />
            </Box>
          </Alert>

          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2}><strong>Grupo de enfermedad</strong></TableCell>
                  <TableCell colSpan={4} align="center" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>Casos</strong></TableCell>
                  <TableCell colSpan={3} align="center" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>Tratamiento</strong></TableCell>
                  <TableCell colSpan={4} align="center" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>Resultado</strong></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>N</strong></TableCell>
                  <TableCell align="right">consulta</TableCell>
                  <TableCell align="right">hospital.</TableCell>
                  <TableCell align="right">cirugía</TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}>farmacológico</TableCell>
                  <TableCell align="right">quirúrgico</TableCell>
                  <TableCell align="right">días hosp.</TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}>complicaciones</TableCell>
                  <TableCell align="right">fallecimiento</TableCell>
                  <TableCell align="right">alta médica</TableCell>
                  <TableCell align="right">no regresa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const esSinClasificar = row.grupo === SIN_CLASIFICAR;
                  const vacia = row.n === 0;
                  return (
                    <TableRow
                      key={row.grupo}
                      sx={{
                        bgcolor: esSinClasificar ? 'warning.light' : undefined,
                        opacity: vacia && !esSinClasificar ? 0.45 : 1,
                      }}
                    >
                      <TableCell sx={{ fontWeight: esSinClasificar ? 700 : 400 }}>{row.grupo}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderLeft: '1px solid rgba(0,0,0,0.12)' }}>{row.n}</TableCell>
                      <TableCell align="right">{row.nConsulta}</TableCell>
                      <TableCell align="right">{row.nHospitalizacion}</TableCell>
                      <TableCell align="right">{row.nCirugia}</TableCell>
                      <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}>{row.farmacologico}</TableCell>
                      <TableCell align="right">{row.nCirugia}</TableCell>
                      <TableCell align="right">{row.diasHospitalizacion}</TableCell>
                      <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}>{row.complicaciones}</TableCell>
                      <TableCell align="right">{row.fallecimiento}</TableCell>
                      <TableCell align="right">{row.altaMedica}</TableCell>
                      <TableCell align="right">{row.noRegresa}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'action.selected' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>{total.n}</strong></TableCell>
                  <TableCell align="right"><strong>{total.nConsulta}</strong></TableCell>
                  <TableCell align="right"><strong>{total.nHospitalizacion}</strong></TableCell>
                  <TableCell align="right"><strong>{total.nCirugia}</strong></TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>{total.farmacologico}</strong></TableCell>
                  <TableCell align="right"><strong>{total.nCirugia}</strong></TableCell>
                  <TableCell align="right"><strong>{total.diasHospitalizacion}</strong></TableCell>
                  <TableCell align="right" sx={{ borderLeft: '1px solid rgba(0,0,0,0.12)' }}><strong>{total.complicaciones}</strong></TableCell>
                  <TableCell align="right"><strong>{total.fallecimiento}</strong></TableCell>
                  <TableCell align="right"><strong>{total.altaMedica}</strong></TableCell>
                  <TableCell align="right"><strong>{total.noRegresa}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            N = consulta + hospitalización + cirugía. Una ficha SOAP con diagnósticos de
            varios grupos aparece en cada uno de ellos, por lo que la columna «consulta»
            puede superar el número de fichas del periodo.
          </Typography>

          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Atenciones y beneficiarios — {year}
          </Typography>

          {attentionBeneficiary.branches.map((branch) => (
            <Paper key={branch.id} sx={{ p: 2, mb: 2 }} variant="outlined">
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {branch.name || 'Sin nombre'}
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180 }}><strong>Tipo de Atención</strong></TableCell>
                      {MESES_LABEL.map((mes) => (
                        <TableCell key={mes} align="right" sx={{ minWidth: 70 }}><strong>{mes}</strong></TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['Consultas', 'Cirugías', 'Egresos Hospital', 'Total Atenciones', 'N° de Beneficiarios'].map((label, idx) => {
                      const keys = ['consultas', 'cirugias', 'egresos', 'total', 'beneficiarios'];
                      const key = keys[idx];
                      return (
                        <TableRow key={label} sx={label === 'N° de Beneficiarios' ? { bgcolor: 'action.hover' } : {}}>
                          <TableCell><strong>{label}</strong></TableCell>
                          {MONTHS.map((m) => (
                            <TableCell key={m} align="right">
                              {attentionBeneficiary.data[branch.id]?.[m]?.[key] ?? 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}

          <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Total todas las sucursales
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180 }}><strong>Tipo de Atención</strong></TableCell>
                    {MESES_LABEL.map((mes) => (
                      <TableCell key={mes} align="right" sx={{ minWidth: 70 }}><strong>{mes}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['Consultas', 'Cirugías', 'Egresos Hospital', 'Total Atenciones', 'N° de Beneficiarios'].map((label, idx) => {
                    const keys = ['consultas', 'cirugias', 'egresos', 'total', 'beneficiarios'];
                    const key = keys[idx];
                    return (
                      <TableRow key={`total-${label}`} sx={label === 'N° de Beneficiarios' ? { bgcolor: 'action.hover' } : {}}>
                        <TableCell><strong>{label}</strong></TableCell>
                        {MONTHS.map((m) => (
                          <TableCell key={m} align="right">
                            {attentionBeneficiary.totals[m]?.[key] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {unresolved.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="warning.main" gutterBottom>
                Registros que no se pudieron clasificar ({unresolvedTotal})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Estos valores no existen en el catálogo <code>diagnoses</code>. Agregarlos
                al catálogo (o corregir el código del registro) los incorpora al grupo
                correcto de forma automática.
              </Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Fuente y valor original</strong></TableCell>
                      <TableCell align="right"><strong>Cantidad</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unresolved.slice(0, 100).map((u) => (
                      <TableRow key={u.name}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{u.name}</TableCell>
                        <TableCell align="right">{u.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {unresolved.length > 100 && (
                <Typography variant="caption" color="text.secondary">
                  Mostrando los 100 más frecuentes. Descarga el CSV para ver los {unresolved.length}.
                </Typography>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default HospitalCasuistryTab;
